import type { SegmentDTO } from '@next-space/fe-api-idl';
import { BlockType, TextType } from '@next-space/fe-api-idl';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { message } from 'src/common/components/message';
import { request } from 'src/common/request';
import { createView } from 'src/editor/component/menu-list';
import { segmentsToText } from 'src/editor/utils/editor';
import { sortRecord } from 'src/hooks/block/use-sort-record';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { addBlock } from 'src/redux/managers/block/add';
import { archiveBlock } from 'src/redux/managers/block/archive';
import { covertToSyncBlock } from 'src/redux/managers/block/convert';
import { updateBlock } from 'src/redux/managers/block/update';
import { blocksActions } from 'src/redux/reducers/blocks';
import { cache, dispatch } from 'src/redux/store';
import type { NextBlock, NextWhere, SelectBlock } from 'src/redux/types';
import { $currentUserCache } from 'src/services/user/current-user';
import { sequence } from 'src/utils/async-utils';
import {
  isCollection,
  isLinkPageBlock,
  isLinkTableBlock,
  isPageLike,
} from 'src/utils/block-type-utils';
import { dataNeedForCopy } from 'src/utils/copy-utils';
import { querySelectorFromMainContent } from 'src/utils/dom';
import { getDynamicPageId } from 'src/utils/getPageId';
import { getLocationOrigin } from 'src/utils/location-utils';
import { idsToSelectedBlocks, selectedBlocksToIds } from 'src/utils/select-block-util';
import { v4 as uuidV4 } from 'uuid';
import type { BlockNode, PasteHook } from '../types';
import { Action } from '../types';
import { common } from './common';
import { getSpaceCapacity } from 'src/services/capacity';

export const pasteBlock = (
  { transaction, pasteSyncBlock, selectedBlockHistory, openModal, fetchPage }: PasteHook,
  blockNodes: BlockNode[],
  userId: string,
  spaceId: string,
  fromType?: Action,
  sourcePageId?: string,
  hideSyncMenu?: boolean
) => {
  const {
    syncId,
    pageId,
    pageBlock,
    clearEmptyBlock,
    pos,
    posBlock,
    selectedBlocks,
    where,
    viewId,
  } = common();

  const findCurrentPageId = (id: string) => {
    const found = blockNodes.find((b) => b.id === id);
    if (found) return true;
    !!blockNodes.find((b) => {
      return Object.values(b.subTree).find((o) => o.uuid === id);
    });
  };
  const found = findCurrentPageId(pageId);
  if (found) {
    message.warning({
      content: '当前页面正在剪切板内，可能会导致粘贴的数据不完整，建议在其他页面进行粘贴。',
      duration: 6000,
    });
    return;
  }
  if (!pageBlock) return;

  const pasteInCollectionViewPage =
    pageBlock.type === BlockType.COLLECTION_VIEW_PAGE ||
    pageBlock.type === BlockType.COLLECTION_VIEW;

  let pasteInCollectionView = false;

  // 是否是同一个账号的复制粘贴，不同账号的情况下，页面块一律改为链接
  const isCopyToSameAccount = userId === $currentUserCache.uuid;
  const isCopyToSameSpace = spaceId === getCurrentSpaceId();
  if (posBlock) {
    if (viewId) {
      const parentViewId = cache.collectionViews[viewId]?.parentId;
      if (parentViewId) {
        const parentBlock = cache.blocks[parentViewId];
        pasteInCollectionView =
          parentBlock?.type === BlockType.COLLECTION_VIEW ||
          parentBlock?.type === BlockType.REFERENCE_COLLECTION;
      }
    }
  }

  // TODO: 暂时不支持粘贴除 page 块进 多维表
  if (pasteInCollectionView || pasteInCollectionViewPage) {
    if (blockNodes.some((node) => node.subTree[node.id]?.type !== BlockType.PAGE)) return;
  }

  // 由于引用page和同步块粘贴后需要数据展示，因此需要把剪切板的同步块数据塞到store上
  const fillBlockDataToStore = (subTree: BlockNode['subTree'], uuid: string) => {
    const map: Record<string, NextBlock> = {};
    const loop = (uuid: string) => {
      let block = subTree[uuid];
      if (!block) return;
      if (isLinkPageBlock(block) || block.type === BlockType.SYNC_REFERENCE) {
        map[block.uuid] = block;
        block = subTree[block.data.ref?.uuid ?? ''];
        if (!block) return;
      }
      map[block.uuid] = block;
      const isPage =
        block.type === BlockType.PAGE ||
        block.type === BlockType.COLLECTION_VIEW_PAGE ||
        block.type === BlockType.FOLDER;

      if (isPage) return;
      block.subNodes.forEach(loop);
    };
    loop(uuid);
    dispatch(blocksActions.update({ blocks: map }));
  };

  const isDrive = pageBlock.type === BlockType.FOLDER;

  // 收集页面
  const newPages: { uuid: string; targetId: string }[] = [];
  // 新老 id 对照
  const idMap = new Map();

  const loop = (
    subTree: BlockNode['subTree'],
    id: string,
    newWhere: NextWhere,
    prefixName?: boolean
  ) => {
    const _block = subTree[id];
    if (!_block) return [id];
    if (isDrive && _block.type !== BlockType.FOLDER && _block.type !== BlockType.FILE) {
      return [id];
    }
    let { uuid } = _block;
    let subNodes = [..._block.subNodes];
    let rest = dataNeedForCopy(_block);
    rest.data.defaultTemplateId = _block.data.defaultTemplateId;
    const newInlinePages: Record<string, string> = {};

    if (isCopyToSameAccount) {
      const collectInlinePage = (segments?: SegmentDTO[]) => {
        if (!segments) return;
        segments.forEach((segment) => {
          if (segment.type === TextType.LINK_PAGE && segment.uuid) {
            const inlinePage = cache.blocks[segment.uuid];
            if (inlinePage && inlinePage.parentId === id) {
              newInlinePages[inlinePage.uuid] = newInlinePages[inlinePage.uuid] ?? uuidV4();
              segment.uuid = newInlinePages[inlinePage.uuid];
            }
          }
        });
      };
      collectInlinePage(_block.data.segments);
      collectInlinePage(_block.data.caption);
    } else {
      // 页面的page改成超链接
      if (isPageLike(rest.type) || isLinkPageBlock(rest)) {
        // 不同帐户下，页面改成超链
        if (isLinkPageBlock(rest)) {
          const refBlock = subTree[rest.data.ref?.uuid ?? ''];
          if (!refBlock) return [];
          uuid = refBlock.uuid;
          rest = dataNeedForCopy(refBlock);
        }
        rest.type = BlockType.TEXTAREA;
        const text = segmentsToText(rest.data.segments);
        rest.data = {
          segments: [
            {
              type: TextType.URL,
              text,
              url: `${getLocationOrigin()}/${uuid}`,
              enhancer: {},
            },
          ],
        };
      }

      // 行内引用页面换成超链
      const changeLinkPageToUrl = (segments?: SegmentDTO[]) => {
        if (!segments) return;
        segments.forEach((segment) => {
          if (segment.type === TextType.LINK_PAGE) {
            const refBlock = subTree[segment.uuid ?? ''];
            if (refBlock) {
              const text = segmentsToText(refBlock.data.segments);
              segment.type = TextType.URL;
              segment.url = `${getLocationOrigin()}/${refBlock.uuid}`;
              segment.text = text;
              segment.enhancer = {};
            }
          }
        });
      };

      changeLinkPageToUrl(rest.data.segments);
      changeLinkPageToUrl(rest.data.caption);
    }

    const isPage =
      rest.type === BlockType.PAGE ||
      rest.type === BlockType.COLLECTION_VIEW_PAGE ||
      rest.type === BlockType.FOLDER ||
      rest.type === BlockType.COLLECTION_VIEW ||
      rest.type === BlockType.MIND_MAPPING ||
      rest.type === BlockType.MIND_MAPPING_PAGE;

    if (prefixName && isPage && !pasteInCollectionView) {
      const segments = rest.data.segments ?? [];
      if (segments.length) {
        rest.data.segments = segments;
      }
    }

    fillBlockDataToStore(subTree, uuid);

    const loopSubNodes = (_subNodes: string[]) => {
      const newsIds: string[] = [];
      // 每次都加到最前面，所以把subNodes反转一下再加，要不然每次都要记录前一个block的id
      [..._subNodes].reverse().forEach((uuid) => {
        const newSubNodeId = loop(subTree, uuid, newWhere);
        newsIds.push(...newSubNodeId);
      });
      return newsIds;
    };

    // 同步块复制粘贴逻辑
    // https://flowus.cn/4344e935-df3e-40dc-a00a-2e1ca379e110
    if (rest.type === BlockType.SYNC_CONTAINER || rest.type === BlockType.SYNC_REFERENCE) {
      // 如果是剪切就走通用block复制逻辑
      if (isCopyToSameAccount && isCopyToSameSpace) {
        // 如果是在另一个同步块内粘贴 同步块的话，需要把该同步的subnode提出来放上去，同步块不能嵌套
        if (syncId) {
          const syncBlock =
            rest.type === BlockType.SYNC_REFERENCE ? subTree[_block.data.ref?.uuid ?? ''] : _block;
          if (!syncBlock) {
            // eslint-disable-next-line no-console
            __HOST_LOCAL__ && console.warn('some thing wrong!');
            return [id];
          }
          subNodes = [...syncBlock.subNodes];
          return loopSubNodes(subNodes);
        } else if (rest.type === BlockType.SYNC_CONTAINER && fromType === Action.COPY) {
          rest.type = BlockType.SYNC_REFERENCE;
          rest.data.ref = {
            uuid,
          };
          subNodes = [];
        }

        pasteSyncBlock?.();
      } else {
        // 不同帐户或者不同空间下，复制/剪切后粘贴，同步块/影子同步块时需要把subnode提出来，不会复制 同步块/影子同步块本身
        if (rest.type === BlockType.SYNC_REFERENCE) {
          const syncBlock = subTree[_block.data.ref?.uuid ?? ''];
          if (!syncBlock) {
            // eslint-disable-next-line no-console
            __HOST_LOCAL__ && console.warn('some thing wrong!');
            return [id];
          }
          subNodes = [...syncBlock.subNodes];
        }

        const newsIds: string[] = loopSubNodes(subNodes);
        return newsIds;
      }
    }

    if (rest.data.ossName && rest.data.size) {
      const { maxCapacity, currentCapacity } = getSpaceCapacity(getCurrentSpaceId());
      if (currentCapacity > maxCapacity) {
        // 复制出来一个空块并提示空间不足
        rest.data.size = undefined;
        rest.data.ossName = undefined;
        rest.data.segments = [];
        message.warning('目标空间容量已达到上限');
      }
    }
    const newId = addBlock(rest, newWhere, false, false);

    Object.keys(newInlinePages).forEach((_uuid) => {
      const targetId = newInlinePages[_uuid];
      const inlinePage = cache.blocks[_uuid];
      if (targetId && inlinePage) {
        addBlock(
          { ...dataNeedForCopy(inlinePage), uuid: targetId },
          { inline: true, parentId: newId }
        );
        newPages.push({ uuid: _uuid, targetId });
      }
    });

    if (isLinkTableBlock(rest) && rest.data.ref?.uuid) {
      createView(rest.data.ref.uuid, newId);
    }

    if (newId) {
      if (isPage) {
        newPages.push({ uuid, targetId: newId });
      } else if (subNodes.length) {
        subNodes.forEach((uuid) => {
          loop(subTree, uuid, { parentId: newId });
        });
      }
    }

    idMap.set(id, newId);

    // 如果是表格类型，需要把合并单元格数据的 id 换掉
    if (rest.type === BlockType.TABLE) {
      if (rest.data.format) {
        const ranges = rest.data.format.tableBlockRanges;
        if (ranges) {
          const newRange = ranges.map((range) => {
            const { start, end } = range;
            return {
              start: [idMap.get(start?.[0]), start?.[1]],
              end: [idMap.get(end?.[0]), end?.[1]],
            };
          });
          updateBlock(newId, { data: { format: { tableBlockRanges: newRange } } });
        }
      }
    }

    return [newId];
  };

  // 收集新生成 id 用于高亮选择
  const newBlocks: SelectBlock[] = [];
  transaction(
    () => {
      // pos 为标题时
      if (pos === pageId) {
        where.first = true;
      } else if (pos && !where.last) {
        // 导图节点粘贴的话这里不执行（last会被设置为true）
        where.after = pos;
      }
      let tempWhere = { ...where };
      blockNodes.forEach(({ id, subTree }) => {
        const ids = loop(subTree, id, tempWhere, true);
        if (ids.length > 0) {
          tempWhere = { parentId: tempWhere.parentId, after: ids[ids.length - 1] };
        }

        newBlocks.push(...idsToSelectedBlocks(ids, viewId, syncId));
      });

      const sourceBlocks = newBlocks.map((item) => ({ uuid: item.blockId }));
      // 多维表内粘贴需要 pageSort
      if (pos && posBlock) {
        if (isCollection(posBlock.type) || pasteInCollectionView) {
          if (viewId) {
            if (pos === pageId) {
              sortRecord(viewId, sourceBlocks, { first: true });
            } else {
              sortRecord(viewId, sourceBlocks, { after: pos });
            }
          }
        }
      } else {
        if (pasteInCollectionViewPage) {
          if (viewId) {
            sortRecord(viewId, sourceBlocks, {
              last: true,
            });
          }
        }
      }
      clearEmptyBlock();

      const _selected = new Set<SelectBlock>();

      // 补丁: 为了分栏复制粘贴后能选中块
      newBlocks.forEach((block) => {
        const _block = cache.blocks[block.blockId];
        if (_block && _block.type === BlockType.COLUMN_LIST) {
          _block.subNodes.forEach((id) => {
            const columnBlock = cache.blocks[id];
            if (columnBlock) {
              idsToSelectedBlocks(columnBlock.subNodes, viewId, syncId).forEach((o) =>
                _selected.add(o)
              );
            }
          });
        } else {
          _selected.add(block);
        }
      });

      selectedBlockHistory?.([..._selected], selectedBlocks);
    },
    { noThrottle: true }
  );

  // 如果有 page 块调复制接口
  if (newPages.length) {
    void sequence(async () => {
      await request.editor.copySubNodes({ list: newPages });
      // bug:https://flowus.cn/0f621487-0170-4aa5-bdf4-9c77ab624dee
      newPages.forEach(async (pageInfo) => {
        const newBlock = cache.blocks[pageInfo.targetId];
        if (
          newBlock?.type === BlockType.COLLECTION_VIEW ||
          newBlock?.type === BlockType.REFERENCE_COLLECTION
        ) {
          void fetchPage?.(pageInfo.targetId, { force: true });
        }
      });
    });
  }

  if (fromType === Action.CUT) {
    return;
  }
  if (pasteInCollectionViewPage || pasteInCollectionView) return;
  if (syncId) return;
  // 如果剪切板已经有同步块，就不需要再弹窗提示是否变成同步块（同步块之间不能内嵌）
  if (hideSyncMenu) return;
  // 如果是同一个页面粘贴就不往下走
  if (sourcePageId === getDynamicPageId()) return;
  // 如果是不同帐户不能变成同步块
  if (!isCopyToSameAccount) return;
  // 如果是不同空间就不让同步了
  if (!isCopyToSameSpace) return;
  const id = newBlocks[0]?.blockId;
  if (!id) return;

  setTimeout(() => {
    // /等dom渲染完再弹窗
    const isInRight = getDynamicPageId() === getDynamicPageId('right');
    const node = querySelectorFromMainContent(`[data-block-id="${id}"]`, isInRight);
    if (!node) return;
    const firstNewBlockId = newBlocks[0]?.blockId;
    if (!firstNewBlockId) return;
    const firstNewBlockParentId = cache.blocks[firstNewBlockId]?.parentId;
    if (!firstNewBlockParentId) return;
    if (cache.blocks[firstNewBlockId]?.type === BlockType.PAGE) return;
    const items = [
      {
        type: ListItemType.BLOCK_TRANSFORM,
        data: {
          title: '忽略',
        },
      },
      {
        type: ListItemType.LINE,
        data: {},
        disableClick: true,
      },
      {
        type: ListItemType.BLOCK_TRANSFORM,
        data: {
          title: '粘贴并同步',
          icon: 'IcRefresh',
          onClick() {
            // 这是复制的
            const pasteIds = blockNodes.map((b) => b.id);
            // 再新建一个sync reference类型的block
            // 再把同步块的id给塞进ref uuid
            transaction(() => {
              const firstPasteBlock = blockNodes[0];
              const firstPasteBlockParentId =
                firstPasteBlock?.subTree[firstPasteBlock.id]?.parentId;
              if (!firstPasteBlockParentId) return;
              // 先把原来的block转变成同步块
              const newSyncBlockId = covertToSyncBlock(pasteIds, firstPasteBlockParentId);
              if (!newSyncBlockId) return;
              addBlock(
                {
                  type: BlockType.SYNC_REFERENCE,
                  data: {
                    ref: {
                      uuid: newSyncBlockId,
                    },
                  },
                },
                {
                  parentId: firstNewBlockParentId,
                  before: firstNewBlockId,
                }
              );
              archiveBlock(selectedBlocksToIds(newBlocks));
            });
          },
        },
      },
    ];
    openModal?.dropdown({
      popcorn: {
        getBoundingClientRect: () => {
          return node.getBoundingClientRect();
        },
      },
      placement: 'bottom-end',
      content: ({ onCloseModal }) => (
        <ListView
          className="next-modal w-52 py-2"
          items={items}
          onItemClick={() => onCloseModal()}
          onGlobalCaptureKeydown={() => onCloseModal()}
        />
      ),
    });
  }, 0);
};
